import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNetworkConfig } from '../config/contracts';
import { loadOrCreateWallet, getBalances, formatRae } from '../services/tyjoltWallet';

const ONBOARDING_KEY = 'whether-tyjolt-onboarded';

export default function OnboardingTyJolt() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [walletAddr, setWalletAddr] = useState('');
  const [balance, setBalance] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const config = getNetworkConfig();

  useEffect(() => {
    if (config.chainType !== 'tyjolt') return;
    const onboarded = localStorage.getItem(ONBOARDING_KEY);
    if (!onboarded) {
      setVisible(true);
      handleCreateWallet();
    }
  }, [config.chainType]);

  async function handleCreateWallet() {
    setLoading(true);
    try {
      const { wallet, isNew } = await loadOrCreateWallet();
      setWalletAddr(wallet.address);
      if (isNew) {
        // Wait a moment for faucet to process
        await new Promise(r => setTimeout(r, 2000));
      }
      const bal = await getBalances(wallet.address);
      setBalance(formatRae(bal.public));
      setStep(1);
    } catch (e) {
      console.warn('Onboarding wallet creation failed:', e);
    }
    setLoading(false);
  }

  function handleComplete() {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setVisible(false);
  }

  if (!visible) return null;

  const steps = [
    {
      title: 'Creating Your ZK Wallet',
      content: loading ? (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7C4DFF" strokeWidth="2">
              <path d="M12 2v4m0 12v4m-7-7H2m20 0h-3M5.636 5.636l2.121 2.121M16.243 16.243l2.121 2.121M5.636 18.364l2.121-2.121M16.243 7.757l2.121-2.121"/>
            </svg>
          </div>
          <div style={{ color: '#7C4DFF', fontWeight: 600 }}>Setting up your testnet wallet...</div>
        </div>
      ) : null,
    },
    {
      title: 'Wallet Ready',
      content: (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#66BB6A" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div style={{ color: '#66BB6A', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
            Testnet Wallet Created
          </div>
          <div style={{
            background: 'rgba(124,77,255,0.1)',
            border: '1px solid rgba(124,77,255,0.3)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 12,
            fontFamily: 'monospace',
          }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Address</div>
            <div style={{ color: '#7C4DFF', fontWeight: 600 }}>{walletAddr}</div>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#4FC3F7' }}>
            {balance} RAE
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
            Funded from TyJolt testnet faucet
          </div>
        </div>
      ),
    },
    {
      title: 'How to Trade',
      content: (
        <div style={{ textAlign: 'left' }}>
          {[
            { num: '1', text: 'Browse markets — weather, energy, and index derivatives', color: '#4FC3F7' },
            { num: '2', text: 'Open a position — go Long or Short with leverage', color: '#66BB6A' },
            { num: '3', text: 'Watch your P&L — positions settle against oracle data', color: '#FF9800' },
            { num: '4', text: 'Every trade generates a real Jolt ZK proof', color: '#7C4DFF' },
          ].map(s => (
            <div key={s.num} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 0',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 6,
                background: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, flexShrink: 0,
              }}>{s.num}</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{s.text}</div>
            </div>
          ))}
          <div style={{
            marginTop: 16,
            padding: '10px 14px',
            background: 'rgba(124,77,255,0.1)',
            border: '1px solid rgba(124,77,255,0.2)',
            borderRadius: 8,
            fontSize: 12,
            color: 'rgba(255,255,255,0.6)',
          }}>
            ZK Privacy: Shield your funds in the Wallet tab to trade privately.
            Amounts and positions are hidden from validators using NovaBlindFold proofs.
          </div>
        </div>
      ),
    },
  ];

  const currentStep = steps[step] ?? steps[steps.length - 1];
  const isLast = step >= steps.length - 1;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
    }}>
      <div style={{
        background: '#0f1729',
        border: '1px solid rgba(124,77,255,0.3)',
        borderRadius: 16,
        padding: '32px',
        maxWidth: 420,
        width: '90%',
      }}>
        <div style={{
          fontSize: 18, fontWeight: 700, color: '#fff',
          marginBottom: 20, textAlign: 'center',
        }}>
          {currentStep.title}
        </div>

        {currentStep.content}

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginTop: 24,
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {steps.map((_, i) => (
              <div key={i} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: i <= step ? '#7C4DFF' : 'rgba(255,255,255,0.15)',
              }} />
            ))}
          </div>

          {step > 0 && (
            <button
              onClick={() => {
                if (isLast) {
                  handleComplete();
                  navigate('/perps');
                } else {
                  setStep(s => s + 1);
                }
              }}
              style={{
                background: isLast ? '#7C4DFF' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: 8,
                padding: '10px 24px',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {isLast ? 'Start Trading' : 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
